import { create } from 'zustand'
import type { SuggestedAction } from '@/api/plugins'
import type { SessionInfo, ChatMessage } from '@/api/chat'
import { getSessions, getSessionHistory, deleteSession } from '@/api/chat'

// File attachment for multimodal messages
export interface Attachment {
  id: string
  filename: string
  type: 'image' | 'pdf' | 'text'
  size: number
  url: string
  mimeType: string
}

export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  toolCalls?: ToolCall[]
  suggestedActions?: SuggestedAction[]
  isStreaming?: boolean
  isVoice?: boolean // Indicates message came from voice input
  attachments?: Attachment[] // File attachments
}

export interface ToolCall {
  name: string
  arguments: Record<string, unknown>
  result?: unknown
  status: 'pending' | 'running' | 'success' | 'error'
  durationMs?: number // Track execution time
  auditLogId?: number // For feedback system
}

interface ChatState {
  sessionId: string | null
  messages: Message[]
  isLoading: boolean
  error: string | null
  // Session version to prevent race conditions when switching sessions
  sessionVersion: number

  // Streaming state
  streamingStatus: string | null
  streamingToolCalls: ToolCall[]
  streamingStartTime: number | null

  // Session management
  sessions: SessionInfo[]
  sessionsLoading: boolean
  sessionSidebarOpen: boolean

  // Voice overlay state
  voiceOverlayOpen: boolean

  setSessionId: (id: string | null) => void
  addMessage: (message: Message) => void
  updateMessage: (id: string, updates: Partial<Message>) => void
  clearMessages: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void

  // Streaming actions
  setStreamingStatus: (status: string | null) => void
  addStreamingToolCall: (toolCall: ToolCall) => void
  updateStreamingToolCall: (name: string, updates: Partial<ToolCall>) => void
  clearStreamingState: () => void

  // Session management actions
  loadSessions: () => Promise<void>
  loadSession: (sessionId: string) => Promise<void>
  removeSession: (sessionId: string) => Promise<void>
  updateSessionMeta: (sessionId: string, updates: Partial<Pick<SessionInfo, 'summary' | 'message_count'>>) => void
  newChat: () => void
  setSessionSidebarOpen: (open: boolean) => void

  // Real-time message sync
  addMessageFromEvent: (
    sessionId: string,
    messageId: string,
    role: 'user' | 'assistant',
    content: string,
    toolCalls?: ToolCall[],
    timestamp?: Date
  ) => void

  // Voice overlay actions
  setVoiceOverlayOpen: (open: boolean) => void
}

function chatMessageToMessage(msg: ChatMessage, index: number): Message {
  return {
    id: `loaded-${index}`,
    role: msg.role,
    content: msg.content,
    timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
    toolCalls: msg.tool_calls?.map((tc) => ({
      name: tc.name,
      arguments: tc.arguments,
      result: tc.result,
      status: 'success' as const,
      auditLogId: (tc as { audit_log_id?: number }).audit_log_id,
    })),
    suggestedActions: msg.suggested_actions,
  }
}

export const useChatStore = create<ChatState>((set, get) => ({
  sessionId: null,
  messages: [],
  isLoading: false,
  error: null,
  sessionVersion: 0,
  streamingStatus: null,
  streamingToolCalls: [],
  streamingStartTime: null,
  sessions: [],
  sessionsLoading: false,
  sessionSidebarOpen: true,
  voiceOverlayOpen: false,

  setSessionId: (id: string | null) => set({ sessionId: id }),

  addMessage: (message: Message) =>
    set((state) => ({
      messages: [...state.messages, message],
    })),

  updateMessage: (id: string, updates: Partial<Message>) =>
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? { ...msg, ...updates } : msg
      ),
    })),

  clearMessages: () => set((state) => ({
    messages: [],
    sessionId: null,
    sessionVersion: state.sessionVersion + 1,
  })),

  setLoading: (loading: boolean) => set({ isLoading: loading }),

  setError: (error: string | null) => set({ error }),

  // Streaming actions
  setStreamingStatus: (status: string | null) =>
    set((state) => ({
      streamingStatus: status,
      streamingStartTime: status && !state.streamingStartTime ? Date.now() : state.streamingStartTime,
    })),

  addStreamingToolCall: (toolCall: ToolCall) =>
    set((state) => ({
      streamingToolCalls: [...state.streamingToolCalls, toolCall],
    })),

  updateStreamingToolCall: (name: string, updates: Partial<ToolCall>) =>
    set((state) => ({
      streamingToolCalls: state.streamingToolCalls.map((tc) =>
        tc.name === name ? { ...tc, ...updates } : tc
      ),
    })),

  clearStreamingState: () => set({ streamingStatus: null, streamingToolCalls: [], streamingStartTime: null }),

  loadSessions: async () => {
    set({ sessionsLoading: true })
    try {
      const sessions = await getSessions(50, 0)
      set({ sessions, sessionsLoading: false })
    } catch (error) {
      console.error('Failed to load sessions:', error)
      set({ sessionsLoading: false })
    }
  },

  loadSession: async (sessionId: string) => {
    // Increment version and clear messages FIRST to prevent race conditions
    const currentVersion = get().sessionVersion + 1
    set({
      isLoading: true,
      error: null,
      messages: [], // Clear messages immediately
      sessionId, // Set new session ID immediately
      sessionVersion: currentVersion,
    })

    try {
      const history = await getSessionHistory(sessionId)

      // Check if session changed while we were loading (race condition guard)
      if (get().sessionVersion !== currentVersion) {
        console.log('Session changed during load, discarding stale data')
        return
      }

      const messages = history.map(chatMessageToMessage)
      set({ messages, isLoading: false })
    } catch (error) {
      // Only update error if we're still on the same session
      if (get().sessionVersion === currentVersion) {
        console.error('Failed to load session:', error)
        set({ error: 'Failed to load session', isLoading: false })
      }
    }
  },

  removeSession: async (sessionId: string) => {
    try {
      await deleteSession(sessionId)
      const { sessions, sessionId: currentSessionId } = get()
      set({ sessions: sessions.filter((s) => s.id !== sessionId) })

      // If we deleted the current session, start a new chat
      if (currentSessionId === sessionId) {
        get().newChat()
      }
    } catch (error) {
      console.error('Failed to delete session:', error)
    }
  },

  updateSessionMeta: (sessionId: string, updates: Partial<Pick<SessionInfo, 'summary' | 'message_count'>>) => {
    set((state) => ({
      sessions: state.sessions.map((session) =>
        session.id === sessionId
          ? {
              ...session,
              ...updates,
              // Update timestamp to move it to the top when sorted by recency
              updated_at: new Date().toISOString(),
            }
          : session
      ),
    }))
  },

  addMessageFromEvent: (
    sessionId: string,
    messageId: string,
    role: 'user' | 'assistant',
    content: string,
    toolCalls?: ToolCall[],
    timestamp?: Date
  ) => {
    const { sessionId: currentSessionId, messages, isLoading } = get()

    // Only add messages for the currently viewed session
    if (currentSessionId !== sessionId) {
      return
    }

    // Don't add messages while loading (will be included in history fetch)
    if (isLoading) {
      return
    }

    // Check for duplicate (message already exists)
    const isDuplicate = messages.some((msg) => msg.id === messageId)
    if (isDuplicate) {
      return
    }

    // Also check for content match to catch duplicates with different IDs
    // (e.g., message sent by this client and received via SSE)
    const hasMatchingContent = messages.some(
      (msg) =>
        msg.role === role &&
        msg.content === content &&
        // Only consider recent messages (within 5 seconds)
        Math.abs(new Date().getTime() - msg.timestamp.getTime()) < 5000
    )
    if (hasMatchingContent) {
      return
    }

    // Add the message
    const newMessage: Message = {
      id: messageId,
      role,
      content,
      timestamp: timestamp || new Date(),
      toolCalls: toolCalls?.map((tc) => ({
        ...tc,
        status: 'success' as const,
      })),
    }

    set((state) => ({
      messages: [...state.messages, newMessage],
    }))

    console.log('[ChatStore] Added message from event:', messageId, role)
  },

  newChat: () => {
    set((state) => ({
      sessionId: null,
      messages: [],
      error: null,
      sessionVersion: state.sessionVersion + 1,
    }))
  },

  setSessionSidebarOpen: (open: boolean) => set({ sessionSidebarOpen: open }),

  // Voice overlay actions
  setVoiceOverlayOpen: (open: boolean) => set({ voiceOverlayOpen: open }),
}))
